import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { LearnerCalendarResponse, LearnerSession, getLearnerCalendar } from '../../api/learner';
import { KidEmpty } from '../../../../design-system/kid';
import { useLearnerSpace } from '../../learner/LearnerSpace';
import { dayKey, formatDay, formatTime, zoneLabel } from '../../learner/learnerTime';

/** How far ahead the calendar reads — bounded, never every booking ever (SR-04). */
const WINDOW_DAYS = 42;
const DAY = 86_400_000;

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: LearnerCalendarResponse };

/**
 * UC-S07 — the learner's calendar (STU-16-FR-01..06).
 *
 * The events are the shared bookings, read for this learner only; a sibling's
 * never reach the browser (the server scopes the query). Times are drawn in
 * the zone the server chose — the learner's, else the household's, else UTC
 * — and the screen says which (STU-16-FR-03). A rescheduled class shows its
 * new time and says it moved, so this and "Mis clases" never disagree
 * (STU-16-FR-05). Choosing an event opens its class door (STU-16-FR-04).
 */
const LearnerCalendar: React.FC = () => {
  const { studentId } = useLearnerSpace();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  const fetchCalendar = useCallback(() => {
    if (!studentId) return;
    setLoad({ status: 'loading' });
    // Bounded on the server's side of the boundary as well: a day back so a
    // class in progress is still listed, six weeks forward.
    const from = new Date(Date.now() - DAY).toISOString();
    const to = new Date(Date.now() + WINDOW_DAYS * DAY).toISOString();
    getLearnerCalendar(studentId, from, to)
      .then((data) => setLoad({ status: 'ready', data }))
      .catch(() => setLoad({ status: 'error' }));
  }, [studentId]);

  useEffect(fetchCalendar, [fetchCalendar]);

  const days = useMemo(() => {
    if (load.status !== 'ready') return [];
    const tz = load.data.timeZone;
    const groups = new Map<string, LearnerSession[]>();
    for (const s of load.data.calendar) {
      const key = dayKey(s.scheduledStart, tz);
      groups.set(key, [...(groups.get(key) ?? []), s]);
    }
    return Array.from(groups.entries());
  }, [load]);

  return (
    <>
      <h1 className="hi">Mi calendario</h1>
      <p className="hi-s">Tus próximas clases.</p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando tu calendario…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar tu calendario</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchCalendar}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && load.data.calendar.length === 0 && (
        <KidEmpty
          icon="🗓️"
          title="No tienes clases en el calendario"
          detail="Cuando haya una clase con día y hora, aparecerá aquí."
        />
      )}

      {load.status === 'ready' && load.data.calendar.length > 0 && (
        <>
          <p className="lrn-zone">{zoneLabel(load.data.timeZone)}</p>
          {days.map(([key, sessions]) => (
            <section
              className="ag"
              key={key}
              aria-label={formatDay(sessions[0].scheduledStart, load.data.timeZone)}
            >
              <div className="ag-d">
                {formatDay(sessions[0].scheduledStart, load.data.timeZone)}
              </div>
              {sessions.map((s) => (
                <div className="ag-r" key={s.bookingId}>
                  <div className="ag-t">{formatTime(s.scheduledStart, load.data.timeZone)}</div>
                  <div className="ag-i">
                    <b>{s.courseTitle ?? 'Tu clase'}</b>
                    <span>
                      Clase {s.seq}
                      {s.state === 'Rescheduled' ? ' · cambió de hora' : ''}
                    </span>
                  </div>
                  <Link
                    className="ag-b soft"
                    to={`/student/join/${encodeURIComponent(s.bookingId)}`}
                    aria-label={`Abrir la clase ${s.seq} de ${s.courseTitle ?? 'tu clase'}`}
                  >
                    Abrir
                  </Link>
                </div>
              ))}
            </section>
          ))}
        </>
      )}
    </>
  );
};

export default LearnerCalendar;
